import { companyIsSoleTrader } from '~/constants/markets';
import { CountryCode, GlobalErrorType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { cardOfferDTOMappers } from '~/mappers/DTO/cardOffer';
import {
  apiGetCardOffer,
  apiGetKYCQuestionsAndAnswers,
  apiPostCardOffer,
  apiPostKYCAnswers,
} from '~/services/api';
import { PostCardOfferCallback } from '~/types/types';
import { AppDispatch, GetStateFunc } from '..';
import {
  setApiStatus,
  setBeneficialOwners,
  updateForm,
  updateOverview,
} from '../slices/cardOffer.slice';
import { toggleGlobalErrorOn } from '../slices/globalError.slice';
import { ApiStatus } from '../types/sharedTypes';

export const fetchCardOfferData = (clientId: number) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  dispatch(setApiStatus({ offer: ApiStatus.Started }));
  const { cardOffer, intl } = getState();

  if (!cardOffer) return;
  try {
    const res = await apiGetCardOffer(clientId, intl.market);
    if (res) {
      const mappedOffer = cardOfferDTOMappers.offerFromDTO(res);

      dispatch(updateOverview(mappedOffer));
      dispatch(
        updateForm({
          clientId,
          applicationReasons: mappedOffer.applicationReasons,
          signers: mappedOffer.additionalSigners || [],
          applicant: {
            ...mappedOffer.applicant,
            percentage: companyIsSoleTrader(
              intl.market,
              mappedOffer.companyType
            )
              ? '100'
              : '',
          },
        })
      );
      dispatch(setBeneficialOwners(mappedOffer.beneficialOwners));
      dispatch(setApiStatus({ offer: ApiStatus.Completed }));
    } else {
      dispatch(setApiStatus({ offer: ApiStatus.Failed }));
    }
  } catch (err: any) {
    logToSentry(err, 'apiGetCardOffer', {
      clientId,
    });
    dispatch(setApiStatus({ offer: ApiStatus.Failed }));
  }
};

export const postCardOffer = (
  {
    market,
    clientId,
  }: {
    market: CountryCode;
    clientId: number;
  },
  postCardOfferCallback: PostCardOfferCallback
) => async (dispatch: AppDispatch, getState: GetStateFunc) => {
  const {
    cardOffer: { form, apiStatus },
    intl,
  } = getState();
  if (apiStatus.post === ApiStatus.Started) return;
  dispatch(setApiStatus({ post: ApiStatus.Started }));
  const transformedOfferDetails = cardOfferDTOMappers.formToPostOfferDTO(
    form,
    intl.market
  );

  try {
    const response = await apiPostCardOffer({
      data: transformedOfferDetails,
      market,
    });

    postCardOfferCallback(response);
  } catch (err: any) {
    logToSentry(err, 'apiPostCardOfferUserData', {
      clientId,
    });
    dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    dispatch(setApiStatus({ post: ApiStatus.Failed }));
    // TODO: Implement GTM event logging for error
    // dispatch(pushToGtmLoanAction({ stepStatus: StepStatus.Error }));
  }
};

export const fetchKYCQuestionsAndAnswers = () => (
  dispatch: AppDispatch,
  getState: GetStateFunc
): void => {
  dispatch(setApiStatus({ fetchKycQuestions: ApiStatus.Started }));
  const { market } = getState().intl;

  apiGetKYCQuestionsAndAnswers(market)
    .then((data) => {
      if (data) {
        dispatch(updateForm({ kycQuestionnaireQuestions: data.questions }));
      }
      dispatch(setApiStatus({ fetchKycQuestions: ApiStatus.Completed }));
    })
    .catch((err: any) => {
      logToSentry(err, 'apiGetKYCQuestionsAndAnswers', {});
      dispatch(setApiStatus({ fetchKycQuestions: ApiStatus.Failed }));
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    });
};

export const postKYCAnswers = (clientId: number) => async (
  dispatch: AppDispatch,
  getState: GetStateFunc
): Promise<void> => {
  dispatch(setApiStatus({ postKycAnswers: ApiStatus.Started }));
  const { market } = getState().intl;
  const { kycQuestionnaireAnswers } = getState().cardOffer.form;

  try {
    await apiPostKYCAnswers(market, clientId, kycQuestionnaireAnswers);
    dispatch(setApiStatus({ postKycAnswers: ApiStatus.Completed }));
  } catch (error) {
    logToSentry(error, 'apiPostKYCAnswers', {});
    dispatch(setApiStatus({ postKycAnswers: ApiStatus.Failed }));
    dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    // Re-throw so the caller can block card offer submission when KYC fails to save.
    throw error;
  }
};
